import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, CreditCard, AlertCircle, Loader2, Check, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CardElement, useStripe as useStripeJs, useElements } from '@stripe/react-stripe-js';
import { StripeProvider, useStripe, getStripePromise } from '../../../../contexts/StripeContext';
import { Elements } from '@stripe/react-stripe-js';
import { getCurrentUser } from '@aws-amplify/auth';
import GlowingCard from '../../../../components/ui/GlowingCard';
import AchPaymentForm from '../../../../components/stripe/AchPaymentForm';

import { fetchWithAuth, REGISTRATIONS_API } from '../../../../utils/api';
import { getRoutinePrice, formatCurrency as utilFormatCurrency, getRoutineType } from '../../../../utils/competitionUtils';

// Helper function to safely stringify objects with circular references
const safeStringify = (obj) => {
  // For synthetic React events, extract only necessary properties
  if (obj && obj.nativeEvent && obj._reactName) {
    // This is a React synthetic event - don't try to serialize it
    return JSON.stringify({
      eventType: obj._reactName,
      timestamp: new Date().toISOString()
    });
  }
  
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    // Skip browser DOM elements and React internal props
    if (
      key.startsWith('__react') || 
      key === 'stateNode' || 
      key === 'window' ||
      value instanceof HTMLElement || 
      value === window || 
      (typeof value === 'function')
    ) {
      return '[Omitted]';
    }
    
    // Handle circular references
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  });
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

const PaymentStepContent = ({
  event,
  registrationData,
  pricingSettings,
  criteriaSettings,
  updateRegistrationData,
  onNext,
  onBack,
  isDarkMode,
  renderNavigation
}) => {
  // State for payment method and type
  const [paymentMethod, setPaymentMethod] = useState('full'); // 'full' or 'deposit'
  const [paymentType, setPaymentType] = useState('card'); // 'card' or 'ach'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [succeeded, setSucceeded] = useState(false);
  const [cognitoUserId, setCognitoUserId] = useState(null);
  const navigate = useNavigate();
  
  // Get Stripe context and elements
  const { createPayment } = useStripe();
  const stripeJs = useStripeJs();
  const elements = useElements();

  // Determine ACH availability based on event settings
  const [achAvailable, setAchAvailable] = useState(false);
  const [achOnly, setAchOnly] = useState(false);
  const [achPreferred, setAchPreferred] = useState(false);

  // Handle payment settings on load
  useEffect(() => {
    // Debug: Log what we're receiving in payment step
    console.log('🏦 PaymentStep - Registration Data:', registrationData);
    console.log('🏦 PaymentStep - Pricing Data:', registrationData?.pricing);
    console.log('🏦 PaymentStep - Total Amount:', registrationData?.pricing?.total);
    
    if (event && event.paymentSettings) {
      const settings = event.paymentSettings;
      
      // Check if ACH is enabled
      const achEnabled = settings.achEnabled === true;
      
      // Determine ACH availability based on preference and amount
      if (achEnabled) {
        const amount = registrationData?.pricing?.total || 0;
        
        switch (settings.achPreference) {
          case 'ach_only':
            setAchAvailable(true);
            setAchOnly(true);
            setPaymentType('ach');
            break;
            
          case 'both':
            setAchAvailable(true);
            setAchOnly(false);
            break;
            
          case 'ach_preferred':
            setAchAvailable(true);
            setAchOnly(false);
            setAchPreferred(true);
            setPaymentType('ach');
            break;
            
          case 'threshold':
            const meetThreshold = amount >= (settings.achMinimumAmount || 0);
            console.log('🏦 PaymentStep - ACH Threshold Check:', { amount, threshold: settings.achMinimumAmount, meetThreshold });
            if (meetThreshold) {
              setAchAvailable(true);
              setAchOnly(true);
              setPaymentType('ach');
            } else {
              setAchAvailable(false);
              setAchOnly(false);
              setPaymentType('card');
            }
            break;
            
          case 'card_only':
          default:
            setAchAvailable(false);
            setAchOnly(false);
            setPaymentType('card');
            break;
        }
      } else {
        // ACH not enabled
        setAchAvailable(false);
        setAchOnly(false);
        setPaymentType('card');
      }
    } else {
      // No payment settings, default to card only
      setAchAvailable(false);
      setAchOnly(false);
      setPaymentType('card');
    }
  }, [event, registrationData]);

  // Get the actual Cognito user ID on component mount
  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const currentUser = await getCurrentUser();
        const userId = currentUser.userId || currentUser.username;
        console.log("Fetched Cognito user ID:", userId);
        setCognitoUserId(userId);
      } catch (err) {
        console.error('Error fetching user ID:', err);
        setError('Failed to fetch user ID. Please try again.');
      }
    };
    
    fetchUserId();
  }, []);

  // Format currency with safety check
  const formatCurrency = (amount) => {
    // Handle NaN, null, undefined, or invalid numbers
    if (amount === null || amount === undefined || isNaN(amount) || typeof amount !== 'number') {
      return '$0.00';
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

    // Process pricing settings (same logic as ReviewStep)
  const workshopLevels = React.useMemo(() => {
    const registrationPricing =
      pricingSettings?.registrationPricing ||
      pricingSettings?.pricing?.registrationPricing ||
      pricingSettings?.data?.registrationPricing ||
      pricingSettings?.levels ||
      [];

    const normalizedLevels = (registrationPricing || []).map((level) => ({
      id: level.id || level.name || level.label,
      label: level.ageGroup || level.name || level.label,
      price: parseFloat(level.discountRate || level.price || level.cost || 0),
      minAge: level.minAge !== null ? parseInt(level.minAge) : null,
      maxAge: level.maxAge !== null ? parseInt(level.maxAge) : null,
    }));
    return normalizedLevels;
  }, [pricingSettings]);

  const competitionPricingRules = React.useMemo(() => {
    const competitionPricing =
      pricingSettings?.competitionPricing ||
      pricingSettings?.pricing?.competitionPricing ||
      pricingSettings?.data?.competitionPricing ||
      [];

    return competitionPricing || [];
  }, [pricingSettings]);

  // State for calculated totals
  const [totalAmount, setTotalAmount] = useState(0);
  const [calculatedBreakdown, setCalculatedBreakdown] = useState(null);

  // Calculate total amount using useEffect to ensure proper state updates
  useEffect(() => {
    // First try to use existing pricing data
    const existingTotal = registrationData?.pricing?.total || 0;
    const existingBreakdown = registrationData?.pricing?.breakdown || null;

    if (existingTotal > 0) {
      console.log('🏦 PaymentStep - Using existing pricing data:', existingTotal);
      setTotalAmount(existingTotal);
      setCalculatedBreakdown(existingBreakdown);
      return;
    }
    
    // Proper calculation if review step hasn't been completed or pricing is 0
    console.log('🏦 PaymentStep - No pricing data found, calculating proper total...');
    
    let calculatedTotal = 0;
    const breakdown = { dancers: [], teachers: [], observers: [], routines: [] };
    
    // Get participant data (same logic as ReviewStep)
    const dancers = registrationData?.regParticipants?.dancers || registrationData?.dancersFromRoutines || registrationData?.registrationDancers || [];
    const teachers = registrationData?.regParticipants?.teachers || registrationData?.registrationTeachers || [];
    const observers = registrationData?.regParticipants?.observers || registrationData?.registrationObservers || [];
    const routines = registrationData?.routines || [];
    
    // Calculate dancer costs
    dancers.forEach((dancer) => {
      const firstName = dancer.firstName || '';
      const lastName = dancer.lastName || '';
      const dancerName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'Unknown Dancer';
      
      const dancerCost = { name: dancerName, participantId: dancer.dancerId, items: [] };
      const workshopLevel = dancer.workshopLevel || dancer.options?.workshopLevel;
      
      if (workshopLevel) {
        let workshopPrice = dancer.pricing?.basePrice || dancer.calculatedPrice || 0;
        
        if (workshopPrice === 0) {
          const levelPricing = workshopLevels.find(level => 
            level.label === workshopLevel || level.id === workshopLevel);
          
          if (levelPricing) {
            workshopPrice = levelPricing.price || 0;
          }
        }
        
        dancerCost.items.push({
          description: `Workshop Level: ${workshopLevel}`,
          amount: workshopPrice
        });
        calculatedTotal += workshopPrice;
      }
      
      breakdown.dancers.push(dancerCost);
    });
    
    // Calculate routine costs
    routines.forEach((routine) => {
      const routineCost = {
        name: routine.title || 'Untitled Routine',
        routineId: routine.routineCloneId || routine.routineId,
        items: []
      };
      
      let routinePrice = routine.pricing?.basePrice || routine.calculatedPrice || 0;
      
      if (routinePrice === 0) {
        routinePrice = getRoutinePrice(routine, competitionPricingRules);
      }
      
      const dancerCount = Array.isArray(routine.dancers) ? routine.dancers.length : 0;
      const routineType = getRoutineType(dancerCount, competitionPricingRules) || 'Type not set';
      const userStyle = routine.style || 'Style not selected';
      const userCategory = routine.category || 'Category not selected';
      
      const description = `${routineType} - ${userStyle} - ${userCategory}`;
      
      routineCost.items.push({
        description: description,
        amount: routinePrice
      });
      
      calculatedTotal += routinePrice;
      breakdown.routines.push(routineCost);
    });
    
    console.log('🏦 PaymentStep - Calculated total:', {
      dancers: dancers.length,
      teachers: teachers.length,
      observers: observers.length,
      routines: routines.length,
      calculatedTotal,
      breakdown
    });
    
    console.log('🏦 PaymentStep - Setting state:', { calculatedTotal, breakdown });
    setTotalAmount(calculatedTotal);
    setCalculatedBreakdown(breakdown);
  }, [registrationData, workshopLevels, competitionPricingRules]);

  // Additional debug logging for state changes
  useEffect(() => {
    console.log('🏦 PaymentStep - State updated:', { 
      totalAmount, 
      hasBreakdown: !!calculatedBreakdown,
      totalAmountType: typeof totalAmount,
      isNumber: !isNaN(totalAmount)
    });
  }, [totalAmount, calculatedBreakdown]);

  // Debug logging before render
  console.log('🏦 PaymentStep - Pre-render state check:', {
    totalAmount,
    totalAmountType: typeof totalAmount,
    isValidNumber: typeof totalAmount === 'number' && !isNaN(totalAmount)
  });
  
  // Calculate deposit amounts with proper state dependency
  const depositAmount = React.useMemo(() => {
    return event?.depositAmount || totalAmount * 0.25;
  }, [event?.depositAmount, totalAmount]);

  const remainingBalance = React.useMemo(() => {
    return totalAmount - depositAmount;
  }, [totalAmount, depositAmount]);
  
  // Debug: Log deposit calculations
  console.log('🏦 PaymentStep - Deposit Calculations:', {
    totalAmount,
    depositAmount,
    remainingBalance,
    eventDepositAmount: event?.depositAmount,
    usingFallback: (registrationData?.pricing?.total || 0) === 0
  });

  // Handle card payment submission
  const handleCardPayment = async (e) => {
    e.preventDefault();
    
    if (!stripeJs || !elements) {
      setError("Stripe hasn't loaded yet. Please try again.");
      return;
    }
    
    if (!cognitoUserId) {
      setError("User ID not available. Please refresh the page and try again.");
      return;
    }
    
    // Validate event data
    if (!event || !event.id) {
      setError("Event information is missing. Please go back and try again.");
      return;
    }
    
    // Validate event ID
    if (typeof event.id !== 'string' && typeof event.id !== 'number') {
      setError("Invalid event ID format. Please contact support.");
      return;
    }
    
    const eventId = typeof event.id === 'number' ? event.id.toString() : event.id;
    
    // Validate convention owner ID (event creator's ID)
    if (!event.conventionOwnerId) {
      setError("This event doesn't have an owner assigned and cannot accept payments.");
      return;
    }
    
    // Validate registration data
    if (!registrationData || !registrationData.pricing || typeof registrationData.pricing.total !== 'number') {
      setError("Registration data is invalid. Please go back and review your selections.");
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      // Calculate the payment amount based on selected payment method
      const paymentAmount = paymentMethod === 'full' 
        ? totalAmount 
        : depositAmount;
      
      console.log("Event data:", event);
      console.log("Registration data:", registrationData);
      console.log("Current user ID:", cognitoUserId);
      console.log("Convention owner ID:", event.conventionOwnerId);
      console.log("Using event ID:", eventId);
      
      // Create a payment intent with properly formatted data
      const paymentData = {
        amount: Math.round(paymentAmount * 100), // Convert to cents
        eventId: eventId,
        conventionOwnerId: event.conventionOwnerId, // Use the event creator's ID
        registrationData: {
          name: registrationData.dancers?.[0]?.firstName && registrationData.dancers[0]?.lastName
            ? `${registrationData.dancers[0].firstName} ${registrationData.dancers[0].lastName}`
            : cognitoUserId, // Use user ID if name not available
          email: registrationData.dancers?.[0]?.email || '', // No fallback
          eventName: event.name,
          paymentType: paymentMethod === 'full' ? 'Full Payment' : 'Deposit',
          items: [
            {
              description: `${event.name} - ${paymentMethod === 'full' ? 'Full Payment' : 'Deposit'}`,
              amount: Math.round(paymentAmount * 100)
            }
          ]
        },
        paymentMethodType: 'card' // Specify card payment method
      };
      
      console.log("Sending payment data:", JSON.stringify(paymentData, null, 2));
      
      const paymentIntent = await createPayment(paymentData);
      console.log("Payment intent response:", paymentIntent);
      
      if (!paymentIntent || !paymentIntent.clientSecret) {
        throw new Error('Failed to create payment intent. Missing client secret.');
      }
      
      // Confirm the payment with the card element
      const result = await stripeJs.confirmCardPayment(paymentIntent.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: paymentData.registrationData.name,
            email: paymentData.registrationData.email,
          },
        },
      });

      if (result.error) {
        throw result.error;
      }

      if (result.paymentIntent.status === 'succeeded') {
        // Update registration data with payment info
        const paymentData = {
          method: paymentMethod,
          amount: paymentAmount,
          status: 'completed',
          paymentType: 'card',
          paymentIntentId: result.paymentIntent.id,
          date: new Date().toISOString()
        };
        
        // Special update to ensure the database is properly updated with payment completion
        try {
          // Get current user for the update
          const user = await getCurrentUser();
          const userId = user.userId || user.username;
          
          // Prepare complete payment success data
          const completeUpdateData = {
            userId: userId,
            eventId: event.id,
            status: 'completed',
            lastCompletedStep: 'payment',
            currentStepPath: 'success', // Set the current step path to success
            payment: paymentData // Include payment details
          };
          
          console.log("Updating payment completion status with data:", completeUpdateData);
          
          // Update the database directly
          await fetchWithAuth(
            `${REGISTRATIONS_API}/${registrationData.registrationId}`,
            {
              method: "PUT",
              body: safeStringify(completeUpdateData),
            }
          );
          console.log("Card payment update successful");
          
          // Update the registration data with our payment info
          updateRegistrationData('payment', paymentData, false);
          
          setSucceeded(true);
          onNext(); // Navigate to success page
        } catch (error) {
          console.error("Error updating payment status:", error);
          // Still continue to success page even if there was an error
          updateRegistrationData('payment', paymentData);
          setSucceeded(true);
          onNext();
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred during payment processing');
    } finally {
      setLoading(false);
    }
  };

  // Handle ACH payment success
  const handleAchPaymentSuccess = async (paymentIntent) => {
    try {
      // Create payment data
      const paymentData = {
        method: paymentMethod,
        amount: paymentMethod === 'full' 
          ? registrationData.pricing.total 
          : depositAmount,
        status: 'completed', // Mark payment as completed immediately
        paymentType: 'ach',
        paymentIntentId: paymentIntent.id,
        date: new Date().toISOString()
      };
      
      // Save payment data
      updateRegistrationData('payment', paymentData, false);
      
      // Get current user ID for the direct update
      const currentUser = await getCurrentUser();
      const userId = currentUser.userId || currentUser.username;
      
      // Create a more complete update payload
      const updatePayload = {
        userId: userId,
        eventId: event.id,
        status: 'completed',
        lastCompletedStep: 'payment',
        currentStepPath: 'success', // Set the current step path to success
        payment: paymentData
      };
      
      console.log("Sending ACH payment completion update:", updatePayload);
      
      // Update DynamoDB directly
      try {
        await fetchWithAuth(
          `${REGISTRATIONS_API}/${registrationData.registrationId}`,
          {
            method: "PUT",
            body: safeStringify(updatePayload),
          }
        );
        console.log("ACH payment update successful");
      } catch (error) {
        console.error("Error during ACH payment update:", error);
      }
      
      setSucceeded(true);
      onNext();
    } catch (error) {
      console.error("Error handling ACH payment success:", error);
      setSucceeded(true);
      onNext(); // Still continue to success page even if there was an error
    }
  };

  // Handle payment submission (wrapper function)
  const handlePayment = (e) => {
    if (paymentType === 'ach') {
      // ACH is handled by the AchPaymentForm component
      return;
    } else {
      // Process card payment
      handleCardPayment(e);
    }
  };

  return (
    <div className="min-h-full w-full">
      {/* Fixed Header */}
      <div className={`p-4 md:p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <h2 className={`responsive-heading font-bold mb-2 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <CreditCard className="h-6 w-6 mr-2 text-blue-500" />
          Payment
        </h2>
        <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Complete your registration for {event?.name}
        </p>
      </div>
      
      {/* Scrollable Content */}
      <div className="p-4 md:p-6">
        <div className="space-y-8">
          {/* Payment Options */}
          {isDarkMode ? (
            <GlowingCard
              glowColor="from-blue-600/10 via-purple-600/10 to-violet-600/10"
              glowIntensity="blur-xl"
              cardOpacity="bg-[#1a1f2d]/90"
              className="p-6"
            >
              {(() => {
                console.log('🏦 PaymentStep - Passing to PaymentOptionsContent:', {
                  totalAmount,
                  calculatedBreakdown: !!calculatedBreakdown,
                  depositAmount,
                  remainingBalance,
                  currentState: 'dark mode render'
                });
                return null;
              })()}
              <PaymentOptionsContent 
                isDarkMode={isDarkMode}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                paymentType={paymentType}
                setPaymentType={setPaymentType}
                achAvailable={achAvailable}
                achOnly={achOnly}
                achPreferred={achPreferred}
                formatCurrency={formatCurrency}
                registrationData={registrationData}
                event={event}
                totalAmount={totalAmount}
                calculatedBreakdown={calculatedBreakdown}
                depositAmount={depositAmount}
                remainingBalance={remainingBalance}
                handlePayment={handlePayment}
                handleAchPaymentSuccess={handleAchPaymentSuccess}
                error={error}
                succeeded={succeeded}
                loading={loading}
                stripeJs={stripeJs}
                CARD_ELEMENT_OPTIONS={CARD_ELEMENT_OPTIONS}
              />
            </GlowingCard>
          ) : (
            <div className="rounded-lg border bg-base-100 border-base-200">
              {(() => {
                console.log('🏦 PaymentStep - Passing to PaymentOptionsContent:', {
                  totalAmount,
                  calculatedBreakdown: !!calculatedBreakdown,
                  depositAmount,
                  remainingBalance,
                  currentState: 'light mode render'
                });
                return null;
              })()}
              <PaymentOptionsContent 
                isDarkMode={isDarkMode}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                paymentType={paymentType}
                setPaymentType={setPaymentType}
                achAvailable={achAvailable}
                achOnly={achOnly}
                achPreferred={achPreferred}
                formatCurrency={formatCurrency}
                registrationData={registrationData}
                event={event}
                totalAmount={totalAmount}
                calculatedBreakdown={calculatedBreakdown}
                depositAmount={depositAmount}
                remainingBalance={remainingBalance}
                handlePayment={handlePayment}
                handleAchPaymentSuccess={handleAchPaymentSuccess}
                error={error}
                succeeded={succeeded}
                loading={loading}
                stripeJs={stripeJs}
                CARD_ELEMENT_OPTIONS={CARD_ELEMENT_OPTIONS}
              />
            </div>
          )}

        {/* Render standardized navigation */}
        {renderNavigation && renderNavigation(() => handlePayment(), onBack, !loading && elements && (paymentType === 'card'), loading ? "Processing..." : succeeded ? "Payment Complete" : "Complete Payment")}
        </div>
      </div>
    </div>
  );
};

// Payment options content component
const PaymentOptionsContent = ({
  isDarkMode,
  paymentMethod,
  setPaymentMethod,
  paymentType,
  setPaymentType,
  achAvailable,
  achOnly,
  achPreferred,
  formatCurrency,
  registrationData,
  event,
  totalAmount,
  calculatedBreakdown,
  depositAmount,
  remainingBalance,
  handlePayment,
  handleAchPaymentSuccess,
  error,
  succeeded,
  loading,
  stripeJs,
  CARD_ELEMENT_OPTIONS
}) => {
  // Debug: Log what we're receiving in PaymentOptionsContent
  console.log('🏦 PaymentOptionsContent - Received props:', {
    totalAmount,
    calculatedBreakdown: !!calculatedBreakdown,
    depositAmount,
    remainingBalance
  });
  return (
    <div className="p-6">
      {/* Order Summary */}
      <div className={`mb-8 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800/50 border border-white/5' : 'bg-gray-50 border border-gray-200'}`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Order Summary
        </h3>
        
        {/* Event Details */}
        <div className="space-y-2 mb-4">
          <div className="flex justify-between items-center">
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Event</span>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{event?.name}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Date</span>
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{event?.date}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Location</span>
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{event?.location}</span>
          </div>
        </div>
        
        {/* Detailed Pricing Breakdown */}
        <div className={`border-t pt-3 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
          {calculatedBreakdown && (
            <div className="space-y-3 mb-3">
              {/* Dancers */}
              {calculatedBreakdown.dancers?.length > 0 && (
                <div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Dancers ({calculatedBreakdown.dancers.length})
                    </span>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {formatCurrency(calculatedBreakdown.dancers.reduce((sum, d) => sum + d.items.reduce((itemSum, item) => itemSum + item.amount, 0), 0))}
                    </span>
                  </div>
                  {calculatedBreakdown.dancers.map((dancer, index) => (
                    <div key={index} className="ml-4 text-xs space-y-1">
                      <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {dancer.name}: {dancer.items.map(item => `${item.description} (${formatCurrency(item.amount)})`).join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Teachers */}
              {calculatedBreakdown.teachers?.length > 0 && (
                <div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Teachers ({calculatedBreakdown.teachers.length})
                    </span>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {formatCurrency(calculatedBreakdown.teachers.reduce((sum, t) => sum + t.items.reduce((itemSum, item) => itemSum + item.amount, 0), 0))}
                    </span>
                  </div>
                  {calculatedBreakdown.teachers.map((teacher, index) => (
                    <div key={index} className="ml-4 text-xs">
                      <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {teacher.name}: {teacher.items.map(item => `${item.description} (${formatCurrency(item.amount)})`).join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Observers */}
              {calculatedBreakdown.observers?.length > 0 && (
                <div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Observers ({calculatedBreakdown.observers.length})
                    </span>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {formatCurrency(calculatedBreakdown.observers.reduce((sum, o) => sum + o.items.reduce((itemSum, item) => itemSum + item.amount, 0), 0))}
                    </span>
                  </div>
                  {calculatedBreakdown.observers.map((observer, index) => (
                    <div key={index} className="ml-4 text-xs">
                      <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {observer.name}: {observer.items.map(item => `${item.description} (${formatCurrency(item.amount)})`).join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Routines */}
              {calculatedBreakdown.routines?.length > 0 && (
                <div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Competition Routines ({calculatedBreakdown.routines.length})
                    </span>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {formatCurrency(calculatedBreakdown.routines.reduce((sum, r) => sum + r.items.reduce((itemSum, item) => itemSum + item.amount, 0), 0))}
                    </span>
                  </div>
                  {calculatedBreakdown.routines.map((routine, index) => (
                    <div key={index} className="ml-4 text-xs">
                      <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {routine.name}: {routine.items.map(item => `${item.description} (${formatCurrency(item.amount)})`).join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Total Line */}
          <div className={`flex justify-between items-center text-lg font-bold border-t pt-2 ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-200 text-gray-900'}`}>
            <span>Total Due</span>
            <span className="text-primary">
              {(() => {
                console.log('🏦 UI Total Due - Rendering with totalAmount:', totalAmount);
                return formatCurrency(totalAmount);
              })()}
            </span>
          </div>
        </div>
      </div>

      <h3 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-white' : ''}`}>
        Payment Options
      </h3>

      {/* Payment Method Selection (Full/Deposit) */}
      <div className="space-y-4 mb-6">
        {/* Full Payment */}
        <label className={`block p-4 rounded-lg cursor-pointer ${
          paymentMethod === 'full'
            ? isDarkMode
              ? 'bg-blue-500/10 border border-blue-500/20'
              : 'bg-primary/5 border border-primary/20'
            : isDarkMode
              ? 'bg-gray-800/50 border border-white/5 hover:bg-gray-800'
              : 'bg-base-200 hover:bg-base-300'
        }`}>
          <div className="flex items-start">
            <input
              type="radio"
              name="paymentMethod"
              value="full"
              checked={paymentMethod === 'full'}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="radio radio-primary mt-1"
            />
            <div className="ml-3">
              <span className={`block font-medium ${isDarkMode ? 'text-white' : ''}`}>
                Pay in Full
              </span>
              <span className={`block text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-base-content/70'}`}>
                Pay the total amount of {(() => {
                  console.log('🏦 UI Payment Option - Rendering with totalAmount:', totalAmount);
                  return formatCurrency(totalAmount);
                })()}
              </span>
            </div>
          </div>
        </label>

        {/* Deposit */}
        {event?.allowDeposit && (
          <label className={`block p-4 rounded-lg cursor-pointer ${
            paymentMethod === 'deposit'
              ? isDarkMode
                ? 'bg-blue-500/10 border border-blue-500/20'
                : 'bg-primary/5 border border-primary/20'
              : isDarkMode
                ? 'bg-gray-800/50 border border-white/5 hover:bg-gray-800'
                : 'bg-base-200 hover:bg-base-300'
          }`}>
            <div className="flex items-start">
              <input
                type="radio"
                name="paymentMethod"
                value="deposit"
                checked={paymentMethod === 'deposit'}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="radio radio-primary mt-1"
              />
              <div className="ml-3">
                <span className={`block font-medium ${isDarkMode ? 'text-white' : ''}`}>
                  Pay Deposit
                </span>
                <span className={`block text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-base-content/70'}`}>
                  Pay a deposit of {formatCurrency(depositAmount)} now and {formatCurrency(remainingBalance)} later
                </span>
              </div>
            </div>
          </label>
        )}
      </div>

      {/* Payment Type Selection (Card/ACH) */}
      {achAvailable && !achOnly && (
        <div className="mb-6">
          <h4 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Select Payment Method
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {/* Credit Card Option */}
            <label className={`block p-3 rounded-lg cursor-pointer ${
              paymentType === 'card'
                ? isDarkMode
                  ? 'bg-blue-500/10 border border-blue-500/20'
                  : 'bg-primary/5 border border-primary/20'
                : isDarkMode
                  ? 'bg-gray-800/50 border border-white/5 hover:bg-gray-800'
                  : 'bg-base-200 hover:bg-base-300'
            }`}>
              <div className="flex flex-col items-center">
                <input
                  type="radio"
                  name="paymentType"
                  value="card"
                  checked={paymentType === 'card'}
                  onChange={(e) => setPaymentType(e.target.value)}
                  className="radio radio-sm radio-primary mb-2"
                />
                <CreditCard className={`h-5 w-5 mb-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : ''}`}>
                  Credit Card
                </span>
                <span className={`text-xs mt-1 text-center ${isDarkMode ? 'text-gray-400' : 'text-base-content/70'}`}>
                  2.9% + $0.30 fee
                </span>
              </div>
            </label>
            
            {/* ACH Option */}
            <label className={`block p-3 rounded-lg cursor-pointer ${
              paymentType === 'ach'
                ? isDarkMode
                  ? 'bg-green-500/10 border border-green-500/20'
                  : 'bg-success/5 border border-success/20'
                : isDarkMode
                  ? 'bg-gray-800/50 border border-white/5 hover:bg-gray-800'
                  : 'bg-base-200 hover:bg-base-300'
            }`}>
              <div className="flex flex-col items-center">
                <input
                  type="radio"
                  name="paymentType"
                  value="ach"
                  checked={paymentType === 'ach'}
                  onChange={(e) => setPaymentType(e.target.value)}
                  className="radio radio-sm radio-success mb-2"
                />
                <DollarSign className={`h-5 w-5 mb-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : ''}`}>
                  Bank Account
                </span>
                <span className={`text-xs mt-1 text-center ${isDarkMode ? 'text-gray-400' : 'text-base-content/70'}`}>
                  0.8% fee (max $5)
                </span>
              </div>
            </label>
          </div>
          
          {/* Preferred payment badge */}
          {achPreferred && (
            <div className={`mt-2 text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'} flex items-center justify-end`}>
              <span className="font-medium">✨ Bank Account payments preferred by event organizer</span>
            </div>
          )}
        </div>
      )}

      {/* ACH Only Notice */}
      {achOnly && (
        <div className={`mb-6 p-3 rounded-lg ${
          isDarkMode 
            ? 'bg-blue-900/20 border border-blue-800/20' 
            : 'bg-blue-50 border border-blue-100'
        }`}>
          <div className="flex items-start">
            <DollarSign className={`w-5 h-5 mt-0.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <div className="ml-2">
              <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                This payment is only available via Bank Account (ACH) transfer.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Form - conditionally show either ACH or Card form */}
      {paymentType === 'ach' ? (
        <AchPaymentForm
          amount={paymentMethod === 'full' ? registrationData.pricing.total : depositAmount}
          eventId={event?.id}
          conventionOwnerId={event?.conventionOwnerId}
          registrationData={{
            name: registrationData.dancers?.[0]?.firstName && registrationData.dancers[0]?.lastName
              ? `${registrationData.dancers[0].firstName} ${registrationData.dancers[0].lastName}`
              : '',
            email: registrationData.dancers?.[0]?.email || '',
            eventName: event?.name,
            paymentType: paymentMethod === 'full' ? 'Full Payment' : 'Deposit',
          }}
          onSuccess={handleAchPaymentSuccess}
          onError={(err) => console.error('ACH payment error:', err)}
          isDarkMode={isDarkMode}
        />
      ) : (
        <form onSubmit={handlePayment} className="space-y-4">
          <div className={`p-4 rounded-lg border ${
            isDarkMode 
              ? 'border-white/10 bg-gray-800/50' 
              : 'border-base-200 bg-base-200'
          }`}>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Card Details
            </label>
            <CardElement 
              options={CARD_ELEMENT_OPTIONS} 
              className={`p-3 rounded-md ${
                isDarkMode 
                  ? 'bg-gray-700 text-white' 
                  : 'bg-white border border-gray-300'
              }`} 
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className={`p-4 rounded-lg ${
              isDarkMode 
                ? 'bg-red-500/10 border border-red-500/20 text-red-400' 
                : 'alert alert-error'
            }`}>
              <AlertCircle className="h-5 w-5 mr-2 inline-block" />
              <span>{error}</span>
            </div>
          )}

          {/* Success Message */}
          {succeeded && (
            <div className={`p-4 rounded-lg ${
              isDarkMode 
                ? 'bg-green-500/10 border border-green-500/20 text-green-400' 
                : 'alert alert-success'
            }`}>
              <div className="flex items-center">
                <div className="mr-3 flex-shrink-0">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Payment successful!</p>
                  <p className="text-sm">Thank you for your payment. Proceeding to confirmation...</p>
                </div>
              </div>
            </div>
          )}
        </form>
      )}

      {/* Payment Summary */}
      <div className={`pt-6 border-t ${isDarkMode ? 'border-white/10' : 'border-base-200'}`}>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className={isDarkMode ? 'text-gray-400' : 'text-base-content/70'}>Subtotal</span>
            <span className={isDarkMode ? 'text-gray-400' : ''}>
              {(() => {
                console.log('🏦 UI Subtotal - Rendering with totalAmount:', totalAmount);
                return formatCurrency(totalAmount);
              })()}
            </span>
          </div>
          {(registrationData.pricing.discounts || []).map((discount, index) => (
            <div key={index} className="flex justify-between text-sm">
              <span className={isDarkMode ? 'text-gray-400' : 'text-base-content/70'}>
                {discount.description}
              </span>
              <span className={isDarkMode ? 'text-red-400' : 'text-error'}>
                -{formatCurrency(discount.amount)}
              </span>
            </div>
          ))}
          <div className="flex justify-between font-medium pt-2">
            <span className={isDarkMode ? 'text-white' : ''}>
              {paymentMethod === 'deposit' ? 'Deposit Amount' : 'Total'}
            </span>
            <span className={isDarkMode ? 'text-white' : ''}>
              {(() => {
                const amount = paymentMethod === 'deposit' ? depositAmount : totalAmount;
                console.log('🏦 UI Final Total - Rendering with amount:', amount, 'paymentMethod:', paymentMethod);
                return formatCurrency(amount);
              })()}
            </span>
          </div>
          {paymentMethod === 'deposit' && (
            <div className="flex justify-between text-sm pt-2">
              <span className={isDarkMode ? 'text-gray-400' : 'text-base-content/70'}>
                Remaining Balance (due by {event?.balanceDueDate})
              </span>
              <span className={isDarkMode ? 'text-gray-400' : ''}>
                {formatCurrency(remainingBalance)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PaymentStep = (props) => {
  return (
    <StripeProvider>
      <Elements stripe={getStripePromise()}>
        <PaymentStepContent {...props} />
      </Elements>
    </StripeProvider>
  );
};

export default PaymentStep; 